// MyPledges — donor portal pledges page (β.3 / CHG-1499).
//
// Donor-scope pledges history page. Consumes GET /api/v1/me/pledges
// (α.4 / CHG-1488) via mePledgesApi.listMyPledges. Mirror of β.1
// MyGiving.tsx + the admin Pledges generated page.
//
// Brief columns: created_at, total amount, designation, progress, status.
// Progress uses the existing ProgressBar primitive (CHG-1236) inline as
// a custom column render (fulfilled_minor_units / total_amount_minor_units).
// Multi-currency rollup: a conditional banner above the table that
// surfaces per-currency totals when the donor has pledges in 2+
// currencies. Hidden for single-currency donors (the common case).

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ListPage, type ListPageConfig } from "../components/composition/ListPage";
import { ProgressBar } from "../components/composition/ProgressBar";
import { useFormatters } from "../contexts/IntlContext";
import { mePledgesApi } from "../api/me-pledges";
import type { PortalPledgeRead } from "../api/me-pledges";

// Donor-tone status labels — admin labels are similar but we keep this
// file independent so future donor-vs-admin diverges can land here.
const STATUS_LABEL: Record<string, string> = {
  committed: "Committed",
  in_progress: "In progress",
  fulfilled: "Fulfilled",
  lapsed: "Lapsed",
  canceled: "Canceled",
};

function designationLabel(row: PortalPledgeRead): string {
  return row.designation_name ?? "—";
}

function statusColumn(row: PortalPledgeRead): ReactNode {
  return STATUS_LABEL[row.status] ?? row.status;
}

interface CurrencyRollup {
  currency: string;
  total_minor_units: number;
  fulfilled_minor_units: number;
  count: number;
}

/** Group pledges by currency + sum totals. Used by the rollup banner. */
function computeRollup(pledges: PortalPledgeRead[]): CurrencyRollup[] {
  const map = new Map<string, CurrencyRollup>();
  for (const p of pledges) {
    const key = p.currency;
    const cur = map.get(key) ?? {
      currency: key,
      total_minor_units: 0,
      fulfilled_minor_units: 0,
      count: 0,
    };
    cur.total_minor_units += p.total_amount_minor_units;
    cur.fulfilled_minor_units += p.fulfilled_minor_units;
    cur.count += 1;
    map.set(key, cur);
  }
  // Stable currency order (USD first if present, then alphabetical) so the
  // banner doesn't reshuffle between renders.
  return Array.from(map.values()).sort((a, b) => {
    if (a.currency === "USD") return -1;
    if (b.currency === "USD") return 1;
    return a.currency.localeCompare(b.currency);
  });
}

/**
 * Conditional banner above the pledges list. Renders only when the donor
 * has pledges in 2+ currencies — single-currency donors don't need a
 * rollup because the per-row Amount column already conveys the totals.
 *
 * Fetches its own query (`per_page=100`) so a donor with up to 100
 * pledges sees a complete rollup. Donors with >100 pledges see a
 * sampled rollup of their first page; a future polish CHG can iterate.
 */
function PerCurrencyRollup() {
  const { formatCurrency } = useFormatters();
  const { data } = useQuery({
    queryKey: ["me", "pledges", "rollup"] as const,
    queryFn: () => mePledgesApi.listMyPledges({ page: 0, pageSize: 100 }),
    staleTime: 30_000,
  });
  const pledges = data?.items ?? [];
  const rollup = computeRollup(pledges);
  if (rollup.length < 2) return null;
  return (
    <div
      data-testid="per-currency-rollup"
      style={{
        maxWidth: 1280,
        margin: "0 auto 12px",
        padding: "12px 24px",
        background: "var(--surface-2, #f6f7fb)",
        borderRadius: 8,
        display: "flex",
        gap: 24,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <strong style={{ fontSize: 14 }}>Totals by currency:</strong>
      {rollup.map((r) => {
        const pct =
          r.total_minor_units > 0
            ? Math.round((r.fulfilled_minor_units / r.total_minor_units) * 100)
            : 0;
        return (
          <span key={r.currency} style={{ fontSize: 13 }}>
            <strong>{r.currency}:</strong>{" "}
            {formatCurrency(r.fulfilled_minor_units, r.currency)} of{" "}
            {formatCurrency(r.total_minor_units, r.currency)} fulfilled ({pct}%,{" "}
            {r.count} pledge{r.count === 1 ? "" : "s"})
          </span>
        );
      })}
    </div>
  );
}

function progressColumn(row: PortalPledgeRead): ReactNode {
  const total = row.total_amount_minor_units;
  const fulfilled = row.fulfilled_minor_units;
  const pct = total > 0 ? Math.min(100, Math.round((fulfilled / total) * 100)) : 0;
  return (
    <div style={{ minWidth: 140 }}>
      <ProgressBar
        value={pct}
        max={100}
        tone="g-mygiving"
        valueFormatted={`${pct}%`}
      />
    </div>
  );
}

const myPledgesListConfig: ListPageConfig<PortalPledgeRead> = {
  title: "My pledges",
  subtitle:
    "Pledges you've made to causes you support. Progress reflects payments applied so far.",
  queryKey: ["me", "pledges", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    mePledgesApi.listMyPledges({ page, pageSize, sortField, sortDirection, search }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 25,
  emptyState: {
    title: "You don't have any pledges yet",
    subtitle:
      "When you commit to give to a cause over time, your pledges and their progress appear here.",
  },
  columns: [
    {
      field: "created_at",
      label: "Created",
      type: "relative-date",
      sortable: true,
    },
    {
      field: "total_amount_minor_units",
      label: "Amount",
      type: "currency",
      currencyField: "currency",
      sortable: true,
    },
    {
      field: "designation_id",
      label: "Designation",
      type: "custom",
      render: designationLabel,
    },
    {
      field: "fulfilled_minor_units",
      label: "Progress",
      type: "custom",
      render: progressColumn,
    },
    {
      field: "status",
      label: "Status",
      type: "custom",
      sortable: true,
      render: statusColumn,
    },
  ],
};

export function MyPledges() {
  return (
    <>
      <PerCurrencyRollup />
      <ListPage config={myPledgesListConfig} />
    </>
  );
}
